import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { DateAdapter } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { DatePickerComponent } from '@shared/components/date-picker/date-picker.component';
import { OptionalFieldComponent } from '@shared/components/optional-field/optional-field.component';
import { UnitSelectorComponent } from '@shared/components/unit-selector/unit-selector.component';
import { NotificationService } from '@shared/services/notification.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Person } from '../../models/person.model';
import { CreatePersonEventRequest, PersonEvent, PersonEventActivityOption, PersonEventActivityRoleOption } from '../../models/person-event.model';
import { PersonEventService } from '../../services/person-event.service';
import {
  getPersonEventCategoryForKind,
  getPersonEventEditorErrorMessage,
} from '../../pages/person-event-editor-page/person-event-editor.utils';

@Component({
  selector: 'app-person-activity-editor-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    DatePickerComponent,
    OptionalFieldComponent,
    UnitSelectorComponent,
  ],
  templateUrl: './person-activity-editor-form.component.html',
  styleUrl: './person-activity-editor-form.component.scss',
})
export class PersonActivityEditorFormComponent implements OnInit, OnDestroy {
  @Input({ required: true }) person!: Person;
  @Input() activities: PersonEventActivityOption[] = [];
  @Input() activityRoles: PersonEventActivityRoleOption[] = [];
  @Input() currentEvent: PersonEvent | null = null;
  @Input() activityEvents: PersonEvent[] = [];

  @Output() cancelled = new EventEmitter<void>();
  @Output() saved = new EventEmitter<void>();

  readonly form;

  isSaving = false;
  private readonly destroy$ = new Subject<void>();

  get visibleActivityEvents(): PersonEvent[] {
    return this.activityEvents.slice().sort((a, b) => b.eventDate.localeCompare(a.eventDate));
  }

  formatDate(value: string): string {
    if (!value) return '-';
    const date = new Date(`${value}T00:00:00`);
    if (Number.isNaN(date.getTime())) return value;
    return new Intl.DateTimeFormat('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(date);
  }

  constructor(
    private readonly fb: FormBuilder,
    private readonly dateAdapter: DateAdapter<any>,
    private readonly personEventService: PersonEventService,
    private readonly notifications: NotificationService,
  ) {
    this.form = this.fb.group({
      eventDate: [null as any, Validators.required],
      endDate: [null as any],
      organizationUnitId: [null as string | null],
      activityId: [null as number | null, Validators.required],
      activityRoleId: [null as number | null],
      notes: [''],
    });
  }

  ngOnInit(): void {
    this.form.get('activityId')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        const selectedRoleId = this.form.get('activityRoleId')?.value;
        if (!this.filteredActivityRoles.some(role => role.id === selectedRoleId)) {
          this.form.patchValue({ activityRoleId: null }, { emitEvent: false });
        }

        this.syncActivityRoleRequirement();
      });

    if (this.currentEvent) {
      this.form.patchValue({
        eventDate: this.fromIsoDate(this.currentEvent.eventDate),
        endDate: this.fromIsoDate(this.currentEvent.endDate),
        organizationUnitId: this.currentEvent.organizationUnitId ?? null,
        activityId: this.currentEvent.activityId ?? null,
        activityRoleId: this.currentEvent.activityRoleId ?? null,
        notes: this.currentEvent.notes ?? '',
      }, { emitEvent: false });
      this.syncActivityRoleRequirement();
      return;
    }

    this.form.patchValue({
      eventDate: this.dateAdapter.today(),
    }, { emitEvent: false });
    this.syncActivityRoleRequirement();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get filteredActivityRoles(): PersonEventActivityRoleOption[] {
    const activityId = this.form.get('activityId')?.value as number | null;
    if (!activityId) {
      return [];
    }

    return this.activityRoles.filter(role => role.activityId === activityId);
  }

  get availableActivities(): PersonEventActivityOption[] {
    return this.activities;
  }

  get selectedActivityHasRoles(): boolean {
    return this.filteredActivityRoles.length > 0;
  }

  get title(): string {
    return this.currentEvent ? 'Editar colaboración' : 'Nueva colaboración';
  }

  get defaultUnitName(): string {
    return this.person.communityName || 'unidad de pertenencia';
  }

  onCancel(): void {
    this.cancelled.emit();
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.notifications.error('Completa los datos requeridos antes de guardar.');
      return;
    }

    const category = getPersonEventCategoryForKind('activity');
    if (!category) {
      this.notifications.error('No se pudo determinar el tipo de evento.');
      return;
    }

    const raw = this.form.getRawValue();
    const request: CreatePersonEventRequest = {
      category,
      eventDate: this.toIsoDate(raw.eventDate),
      endDate: raw.endDate ? this.toIsoDate(raw.endDate) : undefined,
      organizationUnitId: raw.organizationUnitId || undefined,
      activityId: raw.activityId ? Number(raw.activityId) : undefined,
      activityRoleId: raw.activityRoleId ? Number(raw.activityRoleId) : undefined,
      notes: raw.notes || undefined,
    };

    this.isSaving = true;
    const operation$: import('rxjs').Observable<unknown> = this.currentEvent
      ? this.personEventService.updateEvent(this.person.id, category, this.currentEvent.id, request)
      : this.personEventService.createEvent(this.person.id, request);

    operation$.subscribe({
      next: () => {
        this.isSaving = false;
        this.notifications.success(
          this.currentEvent ? 'Registro actualizado correctamente.' : 'Registro agregado correctamente.',
        );
        this.saved.emit();
      },
      error: (err: unknown) => {
        this.isSaving = false;
        this.notifications.error(getPersonEventEditorErrorMessage(
          err,
          this.currentEvent ? 'No se pudo actualizar el registro.' : 'No se pudo guardar el registro.',
        ));
      },
    });
  }

  private toIsoDate(value: any): string {
    const year = this.dateAdapter.getYear(value);
    const month = this.dateAdapter.getMonth(value) + 1;
    const day = this.dateAdapter.getDate(value);
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }

  private fromIsoDate(value?: string | null): any | null {
    if (!value) {
      return null;
    }

    const [year, month, day] = value.split('-').map(Number);
    if (!year || !month || !day) {
      return null;
    }

    return this.dateAdapter.createDate(year, month - 1, day);
  }

  private syncActivityRoleRequirement(): void {
    const roleControl = this.form.get('activityRoleId');
    if (!roleControl) {
      return;
    }

    if (this.selectedActivityHasRoles) {
      roleControl.setValidators([Validators.required]);
    } else {
      roleControl.clearValidators();
      roleControl.setValue(null, { emitEvent: false });
    }

    roleControl.updateValueAndValidity({ emitEvent: false });
  }

}
